import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { Router } from '@angular/router';

import { AuthService } from '../../../features/auth/services/auth.service';
import { SessionService } from '../../services/session.service';

export type UserMenuDropdownPosition = 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';

/**
 * The signed-in user's avatar and the menu behind it.
 *
 * It used to subscribe to `user$` into four component fields and tear the subscription down in
 * `ngOnDestroy`. It reads the session's signals instead, so what it paints is what the server
 * sent and a cleared session leaves nothing behind — no derivation of its own, and nothing to
 * unsubscribe.
 */
@Component({
  selector: 'app-user-menu',
  standalone: true,
  templateUrl: './user-menu.component.html',
  styleUrl: './user-menu.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:click)': 'onDocumentClick($event)',
    '(document:keydown.escape)': 'closeUserMenu()',
  },
})
export class UserMenuComponent {
  readonly dropdownPosition = input<UserMenuDropdownPosition>('bottom-right');

  protected readonly session = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);

  protected readonly userMenuOpen = signal(false);

  protected toggleUserMenu(): void {
    this.userMenuOpen.update((open) => !open);
  }

  protected closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  protected onLogout(): void {
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }

  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu-wrapper')) {
      this.closeUserMenu();
    }
  }
}
